import {
  CheckCircle2,
  ChevronDown,
  FileDown,
  Loader2,
  Search,
  Settings2,
  TriangleAlert,
  X,
  XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { UpgradeProrationInfo } from '@/components/sales/UpgradeProrationInfo';
import { MultiYearTermDiscountControl } from '@/components/sales/wizard/MultiYearTermDiscountControl';
import { NumberStepper } from '@/components/sales/wizard/NumberStepper';
import { ProductDiscountControl } from '@/components/sales/wizard/ProductDiscountControl';
import { TermYearsSelector } from '@/components/sales/wizard/TermYearsSelector';
import { useToast } from '@/components/ui';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useLeadPdfDownload, useLookupArn, useSaveProductDetails } from '@/hooks';
import { useLeadSubscriptions } from '@/hooks/useOrders';
import {
  getAumPlanTier,
  getAumProductCode,
  useAumProductMap,
  usePricingConfig,
} from '@/hooks/usePricing';
import {
  inr,
  type AdditionalArnInput,
  type ApiSubItem,
  type ProductDiscount,
  type ProductInfo,
  type SetupQuoteResult,
} from '@/lib/sales-metrics';
import { normalizeArn } from '@/utils/arn';
import cn from '@/utils/cn';
import { ADD_ARN_NEEDS_AUM_MESSAGE } from '@/utils/constants';
import {
  ADDITIONAL_ARN_TERM_YEARS,
  canEditProductPriceInQuotation,
  getApiSubItems,
  getDefaultTermYears,
  isApiBundleProductCode,
  isTermBasedWebsiteCode,
  isWebsiteCode,
  MFTOOLS_OLD_CODE,
  clampMultiYearDiscountPercent,
  MFTOOLS_OLD_PRICE_SLABS,
  parseAdditionalArnTermYears,
  resolveProductLineTotal,
  resolveProductUnitPrice,
  WEBSITE_PRICE_SLABS,
} from '@/utils/productTermPricing';

/**
 * Shown under the ARN term buttons until the first quote returns.
 *
 * The catalogue price of ADD_ARN, and only a placeholder: the authoritative
 * figure is `setupQuote.additionalArnFee`, priced server-side (ADR-050).
 */
const ADDITIONAL_ARN_FALLBACK_FEE = 5000;

// ─── Website product group ────────────────────────────────────────────────────
// Every WEBSITE_-prefixed product is mutually exclusive (radio-select): one of
// these at most can appear in `selected` at any time. Detection is by PREFIX
// (isWebsiteCode, shared with the admin "Add website tier" feature) rather
// than a fixed list of codes here - a hardcoded list previously meant a newly
// admin-added tier was invisible to this wizard entirely (fell into the
// generic product list, no mutual exclusivity, no chip/price handling).

type ArnRowStatus = 'idle' | 'checking' | 'valid' | 'invalid';

type ArnRowValidation = {
  status: ArnRowStatus;
  message?: string;
};

type StepProductDetailsProps = Readonly<{
  catalog: ProductInfo[];
  selected: string[];
  setSelected: (v: string[]) => void;
  leadId: number;
  primaryArn: string;
  // AUM (in crores) is sourced read-only from Step 1 (lead_details.arnAum, ADR-017).
  /** Crores. NULL = not entered yet; 0 is a real AUM that prices at the lowest slab. */
  primaryArnAum: number | null;
  // Per-product discounts (ADR-019), keyed by product code.
  productDiscounts: Record<string, ProductDiscount>;
  setProductDiscounts: React.Dispatch<React.SetStateAction<Record<string, ProductDiscount>>>;
  /** Per-line override of the multi-year discount on years 2+ (percent). */
  productMultiYearDiscounts: Record<string, number>;
  setProductMultiYearDiscounts: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  additionalArns: AdditionalArnInput[];
  setAdditionalArns: React.Dispatch<React.SetStateAction<AdditionalArnInput[]>>;
  // Sub-item selections for API bundle products, keyed by product code.
  apiItemSelections: Record<string, ApiSubItem[]>;
  setApiItemSelections: React.Dispatch<React.SetStateAction<Record<string, ApiSubItem[]>>>;
  setupQuote: SetupQuoteResult | null;
  isQuoteLoading: boolean;
  onSaveProceed: () => void;
  isDone: boolean;
  customPrices: Record<string, number>;
  setCustomPrices: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  productTerms: Record<string, number>;
  setProductTerms: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  renewalDiscountPercent: number;
  // True once a payment has been handed over to billing: the payable total is
  // frozen, so product/pricing edits are rejected server-side (ADR-035). Shown
  // as a read-only banner here.
  locked?: boolean;
  // The order being edited (ADR-048). Product lines the client holds on OTHER
  // orders are surfaced as "already subscribed" and cannot be re-sold until
  // those subscriptions are cancelled.
  orderId?: number | null;
  // NON-ARN client (ADKJ-117): AUM-slab products and Additional ARN are hidden
  // from the catalogue - there is no AMFI registration to price or extend. The
  // server refuses them too; hiding is the courtesy, not the rule.
  isNonArn?: boolean;
}>;

const parseLookupAum = (raw?: string): number | undefined => {
  if (raw === undefined || raw.trim() === '') return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
};

export function StepProductDetails({
  leadId,
  catalog,
  selected,
  setSelected,
  primaryArn,
  primaryArnAum,
  productDiscounts,
  setProductDiscounts,
  productMultiYearDiscounts,
  setProductMultiYearDiscounts,
  additionalArns,
  setAdditionalArns,
  apiItemSelections,
  setApiItemSelections,
  setupQuote,
  isQuoteLoading,
  onSaveProceed,
  isDone,
  customPrices,
  setCustomPrices,
  productTerms,
  setProductTerms,
  renewalDiscountPercent,
  locked = false,
  orderId = null,
  isNonArn = false,
}: StepProductDetailsProps) {
  const { toast } = useToast();
  const lookupArn = useLookupArn();
  const { data: subscriptions = [] } = useLeadSubscriptions(leadId);
  const productNameOf = (code: string): string =>
    catalog.find((p) => p.code === code)?.name ?? code;
  // Live lines from other orders. ADD_ARN is excluded: it is a quantity add-on
  // that can always be topped up while an MFBOX plan is live, so listing it as
  // "already subscribed" would wrongly read as blocked.
  const heldElsewhere = subscriptions.filter(
    (s) => !s.cancelled && s.orderId !== orderId && s.productCode !== 'ADD_ARN',
  );
  const [search, setSearch] = useState('');
  const [arnValidation, setArnValidation] = useState<Record<number, ArnRowValidation>>({});
  // Which API bundle product's modal is currently open (null = none).
  const [openApiModal, setOpenApiModal] = useState<string | null>(null);
  const quotationScrollRef = useRef<HTMLDivElement>(null);

  const aumMap = useAumProductMap();
  const planTier = getAumPlanTier(selected, aumMap);
  const hasAumProduct = planTier !== null;

  // Admin-configured price options and API sub-item catalogues (Console →
  // Product Settings). The hardcoded lists remain the fallback while the
  // config loads or when a key has never been saved.
  const { data: pricingConfig } = usePricingConfig();
  const websiteSlabsOf = (code: string): number[] => {
    const configured = pricingConfig?.websitePriceSlabs?.[code];
    if (configured && configured.length > 0) return configured;
    // Only the shipped tiers have a hardcoded fallback; a newer, admin-added
    // tier (WEBSITE_AI included) has none, and an empty array is exactly
    // right for it - it is NOT chip-priced, so this simply means "no chips".
    return WEBSITE_PRICE_SLABS[code] ?? [];
  };
  const apiSubItemsOf = (code: string): ApiSubItem[] => {
    const configured = pricingConfig?.apiSubItems?.[code];
    return configured && configured.length > 0 ? configured : getApiSubItems(code);
  };

  /**
   * Additional ARNs selected with no AUM-based product to attach them to.
   *
   * The server prices the ARN fee only when `hasAumPricing` holds - a plan tier
   * AND a primary AUM (pricing.service#calculateSetupQuote) - because additional
   * ARNs exist to raise the COMBINED AUM that drives the MFBOX slab. On their own
   * there is no slab to move, so the quote comes back a legitimate zero.
   *
   * That is the intended rule, but on this screen it was indistinguishable from a
   * quote that had not loaded yet: both guards below only asked whether the quote
   * was falsy/zero and blamed "pricing calculation - try again in a moment". The
   * retry can never succeed, so an RM adding only additional ARNs is stuck with no
   * idea why.
   *
   * Still correct under the merged (order-scoped) pricing model. The text comes
   * from ADD_ARN_NEEDS_AUM_MESSAGE, the SAME sentence the server's two guards
   * throw (pricing.service's quote refusal and orders.service rule 3), stated
   * here at the point of entry so the RM never has to trigger the round trip to
   * learn it - and never sees the rule worded two different ways.
   *
   * `selected` alone cannot answer the question, which is why the quote has a
   * say here: on an ADD-ON order the MFBOX plan the ARNs attach to was bought on
   * an EARLIER order, so it is not in this cart and never will be. The server
   * knows that (orders.service rule 3 accepts an owned plan) and prices the ARNs;
   * a quote that comes back carrying them is proof the rule is satisfied, and the
   * warning has to go. When the rule really is broken there is no quote at all -
   * both server guards throw 422 - so the flag stays true and the warning stands.
   */
  /*
    A quote in hand is the proof, and the ONLY proof available here, that the
    server accepts this cart: both guards throw 422 on exactly this rule, and
    LeadWizard nulls the quote whenever the request fails.

    Not "did the quote price any ARNs": the rows go through
    getCompleteAdditionalArns, so additionalArnCount and additionalArnAmount are
    both 0 until one row has ARN, name and AUM filled in. On the add-on order
    this rule was relaxed for, that window is the whole time the RM is typing the
    first row - and during it the note claimed changes were unsaved (they were
    saved; rule 3 accepts the owned plan) and both Download Quotation and Save
    and Proceed were blocked, advising an MFBOX plan that rule 2 then refuses.
    Same dead end, one screen earlier.
  */
  const serverAcceptedSelection = setupQuote != null;
  const addArnWithoutAumProduct =
    selected.includes('ADD_ARN') && !hasAumProduct && !serverAcceptedSelection;

  /*
    The note is shown for as long as the rule is unmet - with no additional ARNs
    typed yet, and with valid ones typed - because what clears it is adding an
    MFBOX plan, not filling in the rows. A validated ARN says the ARN exists in
    the CRM; it says nothing about there being a plan for it to attach to.

    It is also the ONLY place this sentence appears while the RM is editing. The
    product autosave is refused by the server for as long as the rule is unmet,
    and toasting each refusal put the same line on screen on every keystroke (and
    twice per keystroke under StrictMode's double effect). So the note carries the
    "not saved" half of the message itself, and LeadWizard stays quiet. The
    explicit actions - Download Quotation, Save and Proceed - still toast once,
    because there the RM is waiting on an answer.

    The genuinely valid case - an add-on order whose MFBOX plan was bought on an
    earlier order - clears it too: the server prices those ARNs and
    `arnsPricedByServer` sees it.
  */
  const showAddArnNeedsAumNote = addArnWithoutAumProduct;

  const flatItems = useMemo(
    () =>
      catalog.filter(
        (p) =>
          selected.includes(p.code) &&
          !aumMap[p.code] &&
          p.code !== 'ADD_ARN' &&
          !isApiBundleProductCode(p.code),
      ),
    [catalog, selected, aumMap],
  );

  const aumItem = useMemo(() => {
    const aumCode = getAumProductCode(selected, aumMap);
    return aumCode ? catalog.find((p) => p.code === aumCode) : undefined;
  }, [catalog, selected, aumMap]);

  const aumTermYears = aumItem
    ? (productTerms[aumItem.code] ?? getDefaultTermYears(aumItem.code, { isAumBased: true }))
    : 1;

  /**
   * Longest term an additional ARN may be bought for.
   *
   * An ARN is an attachment to the AUM plan and is cancelled with it, so buying
   * one for longer than the plan sells a year with nothing to attach to. Null
   * when this order sells no AUM line - the ARNs then ride on a plan from an
   * earlier order whose remaining term is not known here, so nothing is capped.
   */
  const arnTermCap = aumItem ? aumTermYears : null;
  const arnTermOptions = ADDITIONAL_ARN_TERM_YEARS.filter(
    (years) => arnTermCap == null || years <= arnTermCap,
  );

  // Shortening the plan has to pull its ARNs down with it, or the save fails on
  // a rule the RM cannot see - the offending buttons are no longer even
  // rendered, so there would be nothing on screen to explain the error.
  useEffect(() => {
    if (arnTermCap == null) return;
    setAdditionalArns((prev) => {
      const clamped = prev.map((row) => (parseAdditionalArnTermYears(row.termYears) > arnTermCap
        ? { ...row, termYears: arnTermCap }
        : row));
      return clamped.some((row, i) => row !== prev[i]) ? clamped : prev;
    });
  }, [arnTermCap, setAdditionalArns]);

  const setProductTerm = (code: string, years: number) => {
    setProductTerms((prev) => ({ ...prev, [code]: years }));
  };

  /**
   * Sets or clears a line's multi-year discount.
   *
   * `null` DELETES the key rather than writing 0: "not overridden" and
   * "negotiated down to 0%" price identically today but are different sales
   * facts, and only the former should follow the company setting if an admin
   * later changes it.
   */
  const setProductMultiYearDiscount = (code: string, percent: number | null) => {
    setProductMultiYearDiscounts((prev) => {
      if (percent === null) {
        if (!(code in prev)) return prev;
        const next = { ...prev };
        delete next[code];
        return next;
      }
      return { ...prev, [code]: clampMultiYearDiscountPercent(percent, renewalDiscountPercent) };
    });
  };

  const setProductDiscount = (code: string, discount: ProductDiscount | null) => {
    setProductDiscounts((prev) => {
      const next = { ...prev };
      if (!discount || discount.value <= 0) {
        delete next[code];
      } else {
        next[code] = discount;
      }
      return next;
    });
  };

  const ensureProductTerm = (code: string) => {
    const isAum = Boolean(aumMap[code]);
    setProductTerms((prev) =>
      prev[code] !== undefined
        ? prev
        : { ...prev, [code]: getDefaultTermYears(code, { isAumBased: isAum }) },
    );
  };

  const grandTotal = setupQuote?.grandTotal ?? 0;
  const additionalArnCount = additionalArns.length;
  // Per-ARN, per-YEAR fee. Read from the quote so the figure under the term
  // buttons is the one the server actually charged; ADDITIONAL_ARN_FALLBACK_FEE
  // only covers the moment before the first quote comes back.
  const additionalArnFee = setupQuote?.additionalArnFee ?? ADDITIONAL_ARN_FALLBACK_FEE;

  const syncAdditionalArnCount = (nextCount: number) => {
    const safeCount = Math.max(0, Math.min(10, nextCount));
    if (safeCount === additionalArns.length) {
      return;
    }
    if (safeCount === 0) {
      setAdditionalArns([]);
      setArnValidation({});
      setSelected(selected.filter((c) => c !== 'ADD_ARN'));
      return;
    }
    if (safeCount > additionalArns.length) {
      setAdditionalArns([
        ...additionalArns,
        ...Array.from({ length: safeCount - additionalArns.length }, () => ({
          arn: '',
          clientName: '',
          termYears: 1,
        })),
      ]);
      return;
    }
    setAdditionalArns(additionalArns.slice(0, safeCount));
    setArnValidation((prev) => {
      const next: Record<number, ArnRowValidation> = {};
      Object.entries(prev).forEach(([key, value]) => {
        const index = Number(key);
        if (index < safeCount) next[index] = value;
      });
      return next;
    });
  };

  const updateAdditionalArn = (index: number, key: 'arn' | 'clientName', value: string) => {
    setAdditionalArns(
      additionalArns.map((row, rowIndex) => (rowIndex === index ? { ...row, [key]: value } : row)),
    );
    if (key === 'arn') {
      setArnValidation((prev) => ({ ...prev, [index]: { status: 'idle' } }));
    }
  };

  const updateAdditionalArnTerm = (index: number, years: number) => {
    const capped = arnTermCap == null ? years : Math.min(years, arnTermCap);
    setAdditionalArns(
      additionalArns.map((row, rowIndex) =>
        (rowIndex === index ? { ...row, termYears: parseAdditionalArnTermYears(capped) } : row)),
    );
  };

  const updateAdditionalArnAum = (index: number, raw: string) => {
    const parsed = raw.trim() === '' ? undefined : Number(raw);
    const aum = parsed !== undefined && Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
    setAdditionalArns(
      additionalArns.map((row, rowIndex) => (rowIndex === index ? { ...row, aum } : row)),
    );
  };

  const validateAdditionalArnRow = (index: number, arnRaw: string) => {
    const normalized = normalizeArn(arnRaw);
    if (!normalized) {
      if (!arnRaw.trim()) {
        setArnValidation((prev) => ({ ...prev, [index]: { status: 'idle' } }));
        return;
      }
      setArnValidation((prev) => ({
        ...prev,
        [index]: { status: 'invalid', message: 'Enter a valid ARN (1 to 6 digits)' },
      }));
      return;
    }

    const normalizedPrimary = normalizeArn(primaryArn);
    if (normalizedPrimary && normalized === normalizedPrimary) {
      setArnValidation((prev) => ({
        ...prev,
        [index]: { status: 'invalid', message: 'Cannot use the Primary ARN here' },
      }));
      return;
    }

    setArnValidation((prev) => ({ ...prev, [index]: { status: 'checking' } }));
    lookupArn.mutate(
      { arn: arnRaw, excludeLeadId: leadId },
      {
        onSuccess: (result) => {
          const fetchedName = (result.arnHolderName || result.clientName || '').trim();
          const fetchedAum = parseLookupAum(result.arnAum);

          setArnValidation((prev) => ({ ...prev, [index]: { status: 'valid' } }));
          setAdditionalArns((prev) =>
            prev.map((row, rowIndex) => {
              if (rowIndex !== index) return row;
              return {
                ...row,
                clientName: fetchedName || row.clientName,
                aum: result.arnAum !== undefined ? fetchedAum : row.aum,
              };
            }),
          );
        },
        onError: (err) => {
          setArnValidation((prev) => ({
            ...prev,
            [index]: {
              status: 'invalid',
              message: err.message || 'No distributor details found for this ARN',
            },
          }));
        },
      },
    );
  };

  useEffect(() => {
    const el = quotationScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [selected.length, setupQuote?.grandTotal]);

  const toggle = (code: string) => {
    if (selected.includes(code)) {
      // Deselect: remove from list (and clear ADD_ARN state).
      setSelected(selected.filter((c) => c !== code));
      if (code === 'ADD_ARN') {
        setAdditionalArns([]);
        setArnValidation({});
      }
      // Also clear any stored price for website codes.
      if (isWebsiteCode(code)) {
        setCustomPrices((prev) => {
          const next = { ...prev };
          delete next[code];
          return next;
        });
      }
    } else if (isWebsiteCode(code)) {
      // Radio behaviour: remove all other website codes first.
      const withoutWebsite = selected.filter((c) => !isWebsiteCode(c));
      setSelected([...withoutWebsite, code]);
      ensureProductTerm(code);
      // Clear custom prices for any previously selected website code (there is
      // at most one, by the same radio rule this branch enforces - but this is
      // written to clear whichever one it is, not a fixed list of codes).
      setCustomPrices((prev) => {
        const next = { ...prev };
        selected.filter((c) => isWebsiteCode(c)).forEach((wc) => {
          delete next[wc];
        });
        // Pre-select the first (lowest) slab price for non-custom tiers.
        const slabs = websiteSlabsOf(code);
        if (slabs.length > 0) {
          const [firstSlab] = slabs;
          next[code] = firstSlab;
        }
        return next;
      });
    } else if (aumMap[code]) {
      // Radio behaviour: only one AUM / MFBOX slab product at a time.
      const withoutAum = selected.filter((c) => !aumMap[c]);
      setSelected([...withoutAum, code]);
      ensureProductTerm(code);
    } else {
      setSelected([...selected, code]);
      ensureProductTerm(code);
      if (code === 'ADD_ARN' && additionalArns.length === 0) {
        setAdditionalArns([{ arn: '', clientName: '', termYears: 1 }]);
      }
      if (code === MFTOOLS_OLD_CODE) {
        setCustomPrices((prev) =>
          prev[MFTOOLS_OLD_CODE] !== undefined
            ? prev
            : { ...prev, [MFTOOLS_OLD_CODE]: MFTOOLS_OLD_PRICE_SLABS[0] },
        );
      }
    }
    setSearch('');
  };

  // Non-ARN clients cannot buy AUM-slab products or Additional ARN (no AMFI
  // registration to price from or attach to) - keep them out of the list.
  const sellableCatalog = isNonArn
    ? catalog.filter((p) => !aumMap[p.code] && p.code !== 'ADD_ARN')
    : catalog;
  const filtered = sellableCatalog.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase().trim()),
  );

  const { download: downloadQuotation, downloading } = useLeadPdfDownload(leadId);
  const saveProductDetails = useSaveProductDetails();

  const downloadPdf = async () => {
    if (selected.length === 0) {
      toast.error('Select at least one product to generate the quotation');
      return;
    }
    if (addArnWithoutAumProduct) {
      // warning, not error: this is a rule about how the two products go
      // together, not something the RM did wrong. A red box reads as a failure
      // to fix; a yellow one reads as a condition to satisfy.
      toast.warning(ADD_ARN_NEEDS_AUM_MESSAGE);
      return;
    }
    if (!setupQuote?.subtotal) {
      toast.error(
        isQuoteLoading
          ? 'Still calculating the quote - try again in a moment'
          : 'This selection prices to zero, so there is nothing to quote yet',
      );
      return;
    }
    // The server renders the PDF from the persisted product_details. Flush the
    // debounced autosave first (await it) so a click right after an edit renders
    // the current selection/prices, not the last-persisted snapshot.
    try {
      await saveProductDetails.mutateAsync({
        id: leadId,
        payload: {
          selectedProducts: selected,
          customPrices,
          productTerms,
          productDiscounts,
          additionalArns,
        },
      });
    } catch {
      toast.error('Could not save the latest changes - please try again');
      return;
    }
    await downloadQuotation('quotation');
  };

  const isGenerating = downloading === 'quotation' || saveProductDetails.isPending;

  const handleSaveProceed = () => {
    if (selected.length === 0) {
      toast.error('Select at least one product to proceed');
      return;
    }
    // Only a BLANK field blocks the save. A typed 0 is a real figure - the
    // client has no AUM yet - and prices at the lowest slab like any other.
    if (hasAumProduct && primaryArnAum == null) {
      toast.error('Enter the Primary ARN AUM (in Crores) to price the MFBOX product');
      return;
    }
    if (selected.includes('WEBSITE_CUSTOM') && !(customPrices.WEBSITE_CUSTOM > 0)) {
      toast.error('Enter a price for Website (Custom Development) before proceeding');
      return;
    }
    if (selected.includes('ADD_ARN')) {
      const incompleteArnRow = additionalArns.find((row) => {
        const hasArn = Boolean(row.arn.trim());
        const hasName = Boolean(row.clientName.trim());
        const hasAum = row.aum != null && Number.isFinite(row.aum) && row.aum >= 0;
        return hasArn !== hasName || (hasArn && !hasAum);
      });
      if (incompleteArnRow) {
        toast.error('Each additional ARN row needs ARN, client name, and AUM (Cr)');
        return;
      }
      const invalidArnRow = additionalArns.find(
        (row, index) => row.arn.trim() && arnValidation[index]?.status === 'invalid',
      );
      if (invalidArnRow) {
        toast.error('Fix additional ARN validation errors before proceeding');
        return;
      }
    }
    // Checked before the generic quote guard so the RM gets the actionable
    // reason rather than a retry prompt that can never succeed.
    if (addArnWithoutAumProduct) {
      // warning, not error: this is a rule about how the two products go
      // together, not something the RM did wrong. A red box reads as a failure
      // to fix; a yellow one reads as a condition to satisfy.
      toast.warning(ADD_ARN_NEEDS_AUM_MESSAGE);
      return;
    }
    if (!setupQuote) {
      toast.error(
        isQuoteLoading
          ? 'Still calculating the quote - try again in a moment'
          : 'The quote could not be calculated for this selection. Check the products and AUM above.',
      );
      return;
    }
    if (setupQuote.approvalRequired) {
      toast.error('AUM above 1000 Cr requires Sales Head approval. Please contact your manager.');
      return;
    }
    onSaveProceed();
  };

  return (
    <div className="flex flex-col gap-4 min-[1100px]:h-full min-[1100px]:min-h-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold">Product Details</h3>
          <p className="text-xs text-muted-foreground">
            Select products on the left. Your quotation updates live on the right.
          </p>
          {locked && (
            <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-400">
              <TriangleAlert className="h-3.5 w-3.5" />
              Products are locked - a transaction has been recorded for an installment, so the
              quotation total can no longer change.
            </p>
          )}
          {/* What the client already holds from EARLIER orders (ADR-048), so the
              RM can see at a glance what not to re-sell. The server refuses a
              duplicate anyway; this is the explanation shown before they try. */}
          {heldElsewhere.length > 0 && (
            <div className="mt-2 rounded-md border border-border bg-muted/30 px-2.5 py-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Already subscribed
              </p>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {heldElsewhere.map((s) => (
                  <Badge
                    key={`${s.orderId}-${s.productCode}`}
                    variant="secondary"
                    className="font-normal"
                    title={
                      s.dueDate
                        ? `Order #${s.orderNumber} - renews ${s.dueDate}`
                        : `Order #${s.orderNumber} - awaiting service activation`
                    }
                  >
                    {productNameOf(s.productCode)}
                    {s.dueDate && (
                      <span className="ml-1 text-muted-foreground">{`· ${s.dueDate}`}</span>
                    )}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:gap-3">
          {hasAumProduct && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5">
                  AUM{' '}
                  {primaryArnAum != null ? (
                    <span className="font-bold">{primaryArnAum} Cr</span>
                  ) : (
                    <span className="text-muted-foreground font-normal">- Cr</span>
                  )}
                  {isQuoteLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-4">
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-semibold text-foreground">Primary ARN AUM</span>
                    <span className="text-[11px] text-muted-foreground">
                      {aumItem ? `${aumItem.name} slab-based pricing` : 'AUM slab-based pricing'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold text-foreground">
                      {setupQuote?.totalArnAum != null
                        ? `${setupQuote.totalArnAum} Cr`
                        : primaryArnAum != null
                          ? `${primaryArnAum} Cr`
                          : '- Cr'}
                    </span>
                    {isQuoteLoading && (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                    )}
                  </div>
                  <span className="text-[11px] text-muted-foreground">
                    Primary: {primaryArnAum != null ? `${primaryArnAum} Cr` : '- Cr'}
                    {setupQuote?.totalArnAum != null &&
                      setupQuote.totalArnAum !== primaryArnAum && (
                        <span> · Combined with additional ARNs for slab pricing</span>
                      )}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    Primary AUM is set in Step 1 (Lead Details)
                  </span>
                  {setupQuote?.approvalRequired && (
                    <span className="flex items-center gap-1.5 text-xs text-amber-600">
                      <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
                      AUM above 1000 Cr requires Sales Head / CEO approval
                    </span>
                  )}
                  {setupQuote && !setupQuote.approvalRequired && setupQuote.slab && (
                    <div className="rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                      <span className="text-muted-foreground">Band: </span>
                      {setupQuote.slab.band}
                      <span className="mx-1 text-muted-foreground">→ Total</span>
                      <strong>{inr(setupQuote.setupAmount ?? 0)}</strong>
                      {aumTermYears > 1 && setupQuote.setupYearOneAmount != null && (
                        <div className="mt-1 text-[10px] text-emerald-700">
                          Y1 setup {inr(setupQuote.setupYearOneAmount)} + Y2–
                          {aumTermYears} renewal {inr(setupQuote.setupRenewalYearAmount ?? 0)}
                          /yr
                        </div>
                      )}
                    </div>
                  )}
                  {aumItem && (
                    <div className="flex flex-col gap-1.5">
                      <span className="text-xs font-medium text-foreground">Contract term</span>
                      <TermYearsSelector
                        productCode={aumItem.code}
                        value={aumTermYears}
                        onChange={(years) => setProductTerm(aumItem.code, years)}
                        isAumBased
                      />
                    </div>
                  )}
                  {primaryArnAum == null && (
                    <span className="text-[11px] text-amber-600">
                      Set the ARN AUM in Step 1 to calculate the setup fee
                    </span>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void downloadPdf();
            }}
            disabled={selected.length === 0 || isGenerating}
          >
            {isGenerating ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <FileDown className="mr-1.5 h-4 w-4" />
            )}
            Generate Quotation
          </Button>
          <Button size="sm" onClick={handleSaveProceed} disabled={selected.length === 0}>
            {isDone ? 'Update' : 'Save and Proceed'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 min-[1100px]:min-h-0 min-[1100px]:flex-1 min-[1100px]:grid-cols-2 min-[1100px]:grid-rows-[minmax(0,1fr)] min-[1100px]:items-stretch">
        <div className="flex flex-col rounded-lg border border-border bg-background p-4 min-[1100px]:min-h-0">
          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Products
          </h4>
          <div className="relative mb-3">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search products…"
              className="pl-8 pr-8"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="flex flex-col gap-2 pr-1 min-[1100px]:min-h-0 min-[1100px]:flex-1 min-[1100px]:overflow-y-auto">
            {filtered.length === 0 && (
              <div className="py-6 text-center text-xs text-muted-foreground">{`No products match "${search}"`}</div>
            )}
            {/* Non-website products */}
            {filtered
              .filter((p) => !isWebsiteCode(p.code))
              .map((p) => {
                const checked = selected.includes(p.code);
                const isAumBased = !!aumMap[p.code];
                const isApiBundle = isApiBundleProductCode(p.code);
                const isMftoolsOld = p.code === MFTOOLS_OLD_CODE;
                const selectedApiItems = apiItemSelections[p.code] ?? [];
                const apiItemCount = selectedApiItems.length;
                const apiTotal = selectedApiItems.reduce((s, i) => s + i.price, 0);
                return (
                  <div
                    key={p.code}
                    className={cn(
                      'flex flex-col gap-1.5 rounded-md border px-3 py-2 text-sm transition-colors',
                      checked
                        ? 'border-primary/40 bg-primary/5'
                        : 'border-border hover:bg-muted/40',
                    )}
                  >
                    {/* Checkbox row */}
                    <label className="flex cursor-pointer items-center justify-between gap-3">
                      <span className="flex items-center gap-3">
                        <Checkbox checked={checked} onChange={() => toggle(p.code)} />
                        <span className="font-medium">
                          {p.name}
                          {p.code === 'MFTOOLS' && (
                            <span className="ml-1.5 block text-[10px] font-normal text-muted-foreground">
                              {`${inr(resolveProductUnitPrice(p.code, p.price, customPrices))}/yr + GST · free plan available · ${renewalDiscountPercent}% off years 2+`}
                            </span>
                          )}
                          {isAumBased && (
                            <span className="ml-1.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                              AUM
                            </span>
                          )}
                        </span>
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {isAumBased && 'Slab pricing'}
                        {isApiBundle && !checked && 'Choose items'}
                        {isApiBundle && checked && apiItemCount > 0 && (
                          <span className="font-semibold text-primary">{`${apiItemCount} items · ${inr(apiTotal)}`}</span>
                        )}
                        {isApiBundle && checked && apiItemCount === 0 && 'No items selected'}
                        {isMftoolsOld && !checked && `from ${inr(MFTOOLS_OLD_PRICE_SLABS[0])}`}
                        {isMftoolsOld && checked && (
                          <span className="font-semibold text-primary">
                            {inr(customPrices[MFTOOLS_OLD_CODE] ?? MFTOOLS_OLD_PRICE_SLABS[0])}
                          </span>
                        )}
                        {!isAumBased && !isApiBundle && !isMftoolsOld && (
                          <>
                            {inr(resolveProductUnitPrice(p.code, p.price, customPrices))}
                            {p.code === 'MFTOOLS' && '/yr'}
                          </>
                        )}
                      </span>
                    </label>

                    {/* MFTools Old: fixed price chips (no free-text override - the
                      quotation must only ever carry one of the preset slabs). */}
                    {isMftoolsOld && checked && (
                      <div className="ml-7 mt-0.5 flex flex-col gap-1">
                        <span className="text-[10px] text-muted-foreground">Select price</span>
                        <div className="flex flex-wrap gap-1.5">
                          {MFTOOLS_OLD_PRICE_SLABS.map((slab) => {
                            const isActive = customPrices[MFTOOLS_OLD_CODE] === slab;
                            return (
                              <button
                                key={slab}
                                type="button"
                                onClick={() =>
                                  setCustomPrices((prev) => ({ ...prev, [MFTOOLS_OLD_CODE]: slab }))
                                }
                                className={cn(
                                  'rounded-md border px-2.5 py-1 text-xs font-semibold transition-all',
                                  isActive
                                    ? 'border-primary bg-primary text-white shadow-sm'
                                    : 'border-border bg-background text-foreground hover:border-primary/50 hover:bg-primary/5',
                                )}
                              >
                                {inr(slab)}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* API bundle: configure items button */}
                    {isApiBundle && checked && (
                      <div className="ml-7 mt-0.5">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 gap-1.5 text-xs"
                          onClick={() => setOpenApiModal(p.code)}
                        >
                          <Settings2 className="h-3 w-3" />
                          {apiItemCount > 0 ? `${apiItemCount} items selected` : 'Select items'}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}

            {/* ── Website group (radio-select) ── */}
            {filtered.some((p) => isWebsiteCode(p.code)) && (
              <div className="mt-1 rounded-lg border border-border/60 bg-muted/20 p-2">
                <div className="mb-2 flex items-center gap-1.5">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    Website
                  </span>
                  <span className="text-[10px] text-muted-foreground">(choose one)</span>
                </div>
                <div className="flex flex-col gap-1.5">
                  {filtered
                    .filter((p) => isWebsiteCode(p.code))
                    .map((p) => {
                      const wCode = p.code;
                      const checked = selected.includes(wCode);
                      const slabs = websiteSlabsOf(wCode);
                      const termBased = slabs.length === 0 && isTermBasedWebsiteCode(wCode);
                      const currentPrice = customPrices[wCode];

                      return (
                        <div
                          key={wCode}
                          className={cn(
                            'flex flex-col gap-1.5 rounded-md border px-3 py-2 text-sm transition-colors',
                            checked
                              ? 'border-primary/50 bg-primary/5'
                              : 'border-border hover:bg-muted/40',
                          )}
                        >
                          {/* Radio row */}
                          <label className="flex cursor-pointer items-center justify-between gap-3">
                            <span className="flex items-center gap-3">
                              {/* Custom radio bullet */}
                              <span
                                role="radio"
                                aria-checked={checked}
                                tabIndex={0}
                                onClick={() => toggle(wCode)}
                                onKeyDown={(e) => e.key === 'Enter' && toggle(wCode)}
                                className={cn(
                                  'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                                  checked
                                    ? 'border-primary bg-primary'
                                    : 'border-muted-foreground bg-background',
                                )}
                              >
                                {checked && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                              </span>
                              <button
                                type="button"
                                tabIndex={-1}
                                className="cursor-pointer text-left font-medium"
                                onClick={() => toggle(wCode)}
                              >
                                {p.name}
                              </button>
                            </span>
                            {/* Show price badge when not selected; shows control when selected */}
                            {!checked && (
                              <span className="shrink-0 text-xs text-muted-foreground">
                                {slabs.length > 0
                                  ? `from ${inr(slabs[0])}`
                                  : termBased
                                    ? `${inr(resolveProductUnitPrice(wCode, p.price, customPrices))}/yr`
                                    : 'Custom price'}
                              </span>
                            )}
                          </label>

                          {/* Sub-controls shown only when this tier is selected */}
                          {checked && (
                            <div className="ml-7 mt-0.5">
                              {slabs.length > 0 ? (
                                /* Dropdown for Standard / Mid-level / Advance */
                                <div className="flex flex-col gap-1">
                                  <span className="text-[10px] text-muted-foreground">
                                    Select price
                                  </span>
                                  <div className="flex flex-wrap gap-1.5">
                                    {slabs.map((slab) => (
                                      <button
                                        key={slab}
                                        type="button"
                                        onClick={() =>
                                          setCustomPrices((prev) => ({ ...prev, [wCode]: slab }))
                                        }
                                        className={cn(
                                          'rounded-md border px-2.5 py-1 text-xs font-semibold transition-all',
                                          currentPrice === slab
                                            ? 'border-primary bg-primary text-white shadow-sm'
                                            : 'border-border bg-background text-foreground hover:border-primary/50 hover:bg-primary/5',
                                        )}
                                      >
                                        {inr(slab)}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              ) : termBased ? (
                                /* Priced per year like an ordinary flat product
                                   (e.g. WEBSITE_AI): the term, discount and
                                   computed price are edited in the Live
                                   Quotation panel, same as every non-website
                                   multi-year product - not re-duplicated here. */
                                <span className="text-[10px] text-muted-foreground">
                                  {`${inr(resolveProductUnitPrice(wCode, p.price, customPrices))}/yr · choose the term length in your quotation below`}
                                </span>
                              ) : (
                                /* Free-text for Custom Development */
                                <div className="flex flex-col gap-1">
                                  <span className="text-[10px] text-muted-foreground">
                                    Enter price (₹)
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <NumberStepper
                                      value={currentPrice ?? 0}
                                      onChange={(val) =>
                                        setCustomPrices((prev) => ({ ...prev, [wCode]: val }))
                                      }
                                      step={500}
                                      hideZero
                                      size="sm"
                                      inputWidthClassName="w-20"
                                      placeholder="e.g. 80000"
                                      ariaLabel="Custom website price"
                                    />
                                    {currentPrice != null && currentPrice > 0 && (
                                      <span className="text-xs font-semibold text-primary">
                                        {inr(currentPrice)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col rounded-lg border border-border bg-background p-4 min-[1100px]:min-h-0">
          <h4 className="mb-3 shrink-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Live Quotation
          </h4>
          {selected.length === 0 ? (
            <div className="grid flex-1 place-items-center text-sm text-muted-foreground">
              Select a product to see your quotation
            </div>
          ) : (
            <>
              <div
                ref={quotationScrollRef}
                className="flex flex-col divide-y divide-border rounded-lg border border-border min-[1100px]:min-h-0 min-[1100px]:flex-1 min-[1100px]:overflow-y-auto [scrollbar-gutter:stable]"
              >
                {/* 1. Additional ARNs count/details Block (only if ADD_ARN product selected) */}
                {selected.includes('ADD_ARN') && (
                  <div className="flex flex-col gap-2 bg-background/50 px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-foreground">Additional ARNs</span>
                      <NumberStepper
                        value={additionalArnCount}
                        onChange={syncAdditionalArnCount}
                        max={10}
                        size="sm"
                        inputWidthClassName="w-8"
                        ariaLabel="Additional ARN count"
                      />
                    </div>
                    {/* Shown at the point of entry, not only on Save: without it
                        the rows accept ARNs, names and AUM and then price to zero
                        with no explanation anywhere on screen. */}
                    {showAddArnNeedsAumNote && (
                      <p className="rounded-md border border-warning/40 bg-warning-tint px-2 py-1.5 text-[11px] leading-snug text-ink">
                        {`${ADD_ARN_NEEDS_AUM_MESSAGE} Product changes are not saved until then.`}
                      </p>
                    )}
                    {additionalArns.length > 0 && (
                      <div className="flex flex-col gap-1 max-h-40 overflow-y-auto pr-0.5">
                        {additionalArns.map((row, index) => {
                          const validation = arnValidation[index];
                          return (
                            <div
                              key={index}
                              className="grid grid-cols-[1.25rem_minmax(0,1fr)] items-start gap-1.5 sm:grid-cols-[1.25rem_minmax(0,1fr)_minmax(0,1fr)_4.5rem]"
                            >
                              <span className="text-[10px] font-semibold text-muted-foreground leading-none pt-[9px]">
                                {index + 1}.
                              </span>
                              {/* Below `sm` the row template only has the index
                                  track plus one content track, so the three
                                  fields stack inside this wrapper in the second
                                  column - nothing can be auto-placed into the
                                  1.25rem index track and every field keeps the
                                  full content width. From `sm` up the wrapper is
                                  `display: contents`, so the fields become direct
                                  grid items again and fill the four-column
                                  template exactly as before. */}
                              <div className="flex min-w-0 flex-col gap-1.5 sm:contents">
                                <div className="flex min-w-0 flex-col gap-0.5">
                                  <Input
                                    placeholder="ARN"
                                    value={row.arn}
                                    onChange={(e) =>
                                      updateAdditionalArn(index, 'arn', e.target.value)
                                    }
                                    onBlur={(e) => validateAdditionalArnRow(index, e.target.value)}
                                    className="h-7 px-2 py-0.5 text-xs sm:text-xs"
                                  />
                                  {validation?.status === 'checking' && (
                                    <span className="flex items-center gap-0.5 text-[9px] text-muted-foreground">
                                      <Loader2 className="h-2.5 w-2.5 animate-spin" />
                                      Fetching...
                                    </span>
                                  )}
                                  {validation?.status === 'valid' && (
                                    <span className="flex items-center gap-0.5 text-[9px] text-emerald-700">
                                      <CheckCircle2 className="h-2.5 w-2.5 text-emerald-600" />
                                      Found
                                    </span>
                                  )}
                                  {validation?.status === 'invalid' && (
                                    <span className="flex items-center gap-0.5 text-[9px] text-destructive">
                                      <XCircle className="h-2.5 w-2.5 text-red-500" />
                                      {validation.message ?? 'Invalid'}
                                    </span>
                                  )}
                                </div>
                                <Input
                                  placeholder="Client Name"
                                  value={row.clientName}
                                  onChange={(e) =>
                                    updateAdditionalArn(index, 'clientName', e.target.value)
                                  }
                                  className="h-7 px-2 py-0.5 text-xs sm:text-xs"
                                />
                                <Input
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  placeholder="AUM"
                                  value={row.aum ?? ''}
                                  onChange={(e) => updateAdditionalArnAum(index, e.target.value)}
                                  className="h-7 px-2 py-0.5 text-xs sm:text-xs"
                                  aria-label={`Additional ARN ${index + 1} AUM in crores`}
                                />
                              </div>
                              {/* Term sits on its OWN grid line, spanning the
                                  content columns: it belongs to the ARN above
                                  it, and squeezing a fifth control onto that
                                  row would leave none of them usable on a
                                  phone. The 1.25rem index track stays empty so
                                  the buttons line up under the fields. */}
                              <div className="col-start-1 -mt-0.5 sm:col-start-2 sm:col-span-3">
                                <div className="flex flex-wrap items-center gap-1">
                                  {arnTermOptions.map((years) => {
                                    const active = parseAdditionalArnTermYears(row.termYears) === years;
                                    return (
                                      <button
                                        key={years}
                                        type="button"
                                        onClick={() => updateAdditionalArnTerm(index, years)}
                                        aria-pressed={active}
                                        aria-label={`Additional ARN ${index + 1} term ${years} year${years === 1 ? '' : 's'}`}
                                        className={cn(
                                          'rounded border px-1.5 py-0.5 text-[10px] font-semibold transition-colors',
                                          active
                                            ? 'border-primary bg-primary/10 text-primary'
                                            : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground',
                                        )}
                                      >
                                        {years}Y
                                      </button>
                                    );
                                  })}
                                  <span className="ml-0.5 text-[9px] text-muted-foreground">
                                    {`${inr(additionalArnFee)}/yr`}
                                    {/* Says WHY the longer options are absent.
                                        Without it the missing buttons read as a
                                        bug rather than a rule. */}
                                    {arnTermCap != null && arnTermCap < ADDITIONAL_ARN_TERM_YEARS.length
                                      && ` · max ${arnTermCap}Y (plan term)`}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* 2. Selected Products & Custom Prices Table */}
                <div className="pt-1">
                  {/* min-width so the price table scrolls horizontally on
                      phones instead of squeezing the term/discount controls. */}
                  <Table className="min-w-[480px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead className="text-right w-[170px]">Price</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {aumItem && (
                        <TableRow>
                          <TableCell className="font-medium">
                            <div>{aumItem.name}</div>
                            {setupQuote?.slab && (
                              <div className="mt-0.5 text-[11px] text-muted-foreground font-normal">
                                {setupQuote.slab.band}
                                {aumTermYears > 1 && ` · ${aumTermYears}-year term`}
                              </div>
                            )}
                            {setupQuote?.upgrade && (
                              <div className="mt-0.5 text-[11px] font-normal text-primary">
                                {setupQuote.upgrade.prorated
                                  ? `Upgrade · prorated ${setupQuote.upgrade.monthsRemaining}/${setupQuote.upgrade.periodMonths} months`
                                  : 'Upgrade · full difference (not yet activated)'}
                              </div>
                            )}
                            <TermYearsSelector
                              productCode={aumItem.code}
                              value={aumTermYears}
                              onChange={(years) => setProductTerm(aumItem.code, years)}
                              className="mt-1"
                              isAumBased
                            />
                            {setupQuote?.setupAmount != null &&
                              !setupQuote.approvalRequired &&
                              !aumItem.noDiscount && (
                                <div>
                                  <ProductDiscountControl
                                    discount={productDiscounts[aumItem.code]}
                                    grossLineTotal={setupQuote.setupAmount}
                                    onChange={(d) => setProductDiscount(aumItem.code, d)}
                                    ariaLabel={`${aumItem.name} discount`}
                                  />
                                </div>
                              )}
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {isQuoteLoading && (
                              <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin" />
                            )}
                            {!isQuoteLoading &&
                              setupQuote?.setupAmount != null &&
                              inr(setupQuote.setupAmount)}
                            {/* On an upgrade this figure is neither plan's list
                                price, so the working has to be reachable or the
                                RM cannot explain it to the client. */}
                            {!isQuoteLoading && setupQuote?.upgrade && (
                              <UpgradeProrationInfo
                                proration={setupQuote.upgrade}
                                fromName={
                                  catalog.find(
                                    (p) => p.code === setupQuote.upgrade?.fromProductCode,
                                  )?.name
                                }
                                toName={aumItem.name}
                              />
                            )}
                            {!isQuoteLoading && setupQuote?.approvalRequired && (
                              <span className="text-xs text-amber-600">Approval req.</span>
                            )}
                            {!isQuoteLoading &&
                              !setupQuote?.setupAmount &&
                              hasAumProduct &&
                              primaryArnAum == null && (
                                <span className="text-xs text-muted-foreground font-normal">
                                  Enter AUM
                                </span>
                              )}
                            {!isQuoteLoading &&
                              setupQuote?.setupAmount != null &&
                              aumTermYears > 1 && (
                                <div className="mt-0.5 text-[10px] font-normal text-muted-foreground">
                                  Y1 setup + Y2–
                                  {aumTermYears} renewal
                                </div>
                              )}
                          </TableCell>
                        </TableRow>
                      )}
                      {flatItems.map((p) => {
                        // Precedence (highest wins): this line's own override
                        // (productMultiYearDiscounts, checked inside
                        // resolveProductLineTotal) > the product's OWN
                        // catalogue default > the company-wide setting. Mirrors
                        // the server (pricing.service#calculateSetupQuoteForSelection)
                        // exactly, so the live total shown here is never a
                        // moment's guess ahead of what onboarding will snapshot.
                        const catalogDefaultPercent =
                          p.multiYearDiscountPercent ?? renewalDiscountPercent;
                        const lineTotal = resolveProductLineTotal(
                          p.code,
                          p.price,
                          customPrices,
                          productTerms,
                          catalogDefaultPercent,
                          productMultiYearDiscounts,
                        );
                        const termYears = productTerms[p.code] ?? getDefaultTermYears(p.code);
                        // MFTools Old is priced only via its preset chips, so the
                        // quotation shows the chosen slab read-only.
                        const editable =
                          canEditProductPriceInQuotation(p.code) && p.code !== MFTOOLS_OLD_CODE;
                        return (
                          <TableRow key={p.code}>
                            <TableCell className="font-medium">
                              <div>{p.name}</div>
                              <TermYearsSelector
                                productCode={p.code}
                                value={termYears}
                                onChange={(years) => setProductTerm(p.code, years)}
                                className="mt-1"
                              />
                              {termYears > 1 && (
                                <div>
                                  <MultiYearTermDiscountControl
                                    termYears={termYears}
                                    annualPrice={resolveProductUnitPrice(
                                      p.code,
                                      p.price,
                                      customPrices,
                                    )}
                                    defaultPercent={catalogDefaultPercent}
                                    percent={productMultiYearDiscounts[p.code]}
                                    onChange={(percent) =>
                                      setProductMultiYearDiscount(p.code, percent)}
                                    ariaLabel={`${p.name} multi-year term discount`}
                                  />
                                </div>
                              )}
                              {!p.noDiscount && (
                                <div>
                                  <ProductDiscountControl
                                    discount={productDiscounts[p.code]}
                                    grossLineTotal={lineTotal}
                                    onChange={(d) => setProductDiscount(p.code, d)}
                                    ariaLabel={`${p.name} discount`}
                                  />
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {editable ? (
                                <div className="flex justify-end -mr-1 py-1">
                                  <NumberStepper
                                    value={lineTotal}
                                    onChange={(val) =>
                                      setCustomPrices((prev) => ({ ...prev, [p.code]: val }))
                                    }
                                    step={500}
                                    hideZero
                                    size="sm"
                                    inputWidthClassName="w-16"
                                    placeholder="0"
                                    ariaLabel={`${p.name} price`}
                                  />
                                </div>
                              ) : (
                                <span className="font-semibold">{inr(lineTotal)}</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* API bundle products: one row per selected sub-item */}
                      {selected.filter(isApiBundleProductCode).flatMap((code) => {
                        const bundleItems = apiItemSelections[code] ?? [];
                        const bundleName = catalog.find((p) => p.code === code)?.name ?? code;
                        if (bundleItems.length === 0) {
                          return [
                            <TableRow key={code}>
                              <TableCell className="font-medium text-muted-foreground">
                                {bundleName}
                                <div className="text-[11px] font-normal">No items selected</div>
                              </TableCell>
                              <TableCell className="text-right text-muted-foreground">-</TableCell>
                            </TableRow>,
                          ];
                        }
                        return [
                          <TableRow key={`${code}-hdr`} className="bg-muted/30">
                            <TableCell
                              colSpan={2}
                              className="py-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground"
                            >
                              {bundleName}
                            </TableCell>
                          </TableRow>,
                          ...bundleItems.map((item) => (
                            <TableRow key={item.code}>
                              <TableCell className="pl-4 font-medium">{item.name}</TableCell>
                              <TableCell className="text-right font-semibold">
                                {inr(item.price)}
                              </TableCell>
                            </TableRow>
                          )),
                        ];
                      })}
                      {setupQuote && setupQuote.additionalArnAmount > 0 && (
                        <TableRow>
                          <TableCell className="font-medium">
                            {`Additional ARN x${setupQuote.additionalArnCount}`}
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {inr(setupQuote.additionalArnAmount)}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* 4. Billing Totals Summary - per-product discounts (ADR-019) are
                  summed into a single Discount line so Subtotal − Discount + GST
                  always reconciles to Total. */}
              <div className="mt-2 flex shrink-0 flex-col gap-1 pt-1.5 text-xs">
                {setupQuote && !setupQuote.approvalRequired && setupQuote.subtotal != null && (
                  <>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Subtotal</span>
                      <span>{inr((setupQuote.subtotal ?? 0) + setupQuote.discountAmount)}</span>
                    </div>
                    {setupQuote.discountAmount > 0 && (
                      <div className="flex justify-between font-medium text-emerald-700">
                        <span>Discount</span>
                        <span>-{inr(setupQuote.discountAmount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-muted-foreground">
                      <span>GST ({setupQuote.gstRate}%)</span>
                      <span>{inr(setupQuote.gstAmount ?? 0)}</span>
                    </div>
                  </>
                )}

                <div className="flex justify-between border-t border-border pt-1 text-sm font-bold">
                  <span>Total</span>
                  <span>
                    {setupQuote?.approvalRequired ||
                    (hasAumProduct && primaryArnAum == null && flatItems.length === 0)
                      ? '-'
                      : inr(grandTotal)}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* API bundle item-selection modal */}
      {openApiModal !== null &&
        (() => {
          const modalCode = openApiModal;
          const allItems = apiSubItemsOf(modalCode);
          const selectedItems = apiItemSelections[modalCode] ?? [];
          const selectedSet = new Set(selectedItems.map((i) => i.code));
          const modalProductName = catalog.find((p) => p.code === modalCode)?.name ?? modalCode;
          const modalTotal = selectedItems.reduce((s, i) => s + i.price, 0);

          const toggleItem = (item: ApiSubItem) => {
            setApiItemSelections((prev) => {
              const current = prev[modalCode] ?? [];
              const next = selectedSet.has(item.code)
                ? current.filter((i) => i.code !== item.code)
                : [...current, item];
              const nextTotal = next.reduce((s, i) => s + i.price, 0);
              setCustomPrices((pp) => ({ ...pp, [modalCode]: nextTotal }));
              return { ...prev, [modalCode]: next };
            });
          };

          const selectAll = () => {
            setApiItemSelections((prev) => ({ ...prev, [modalCode]: allItems }));
            setCustomPrices((pp) => ({
              ...pp,
              [modalCode]: allItems.reduce((s, i) => s + i.price, 0),
            }));
          };

          const clearAll = () => {
            setApiItemSelections((prev) => ({ ...prev, [modalCode]: [] }));
            setCustomPrices((pp) => ({ ...pp, [modalCode]: 0 }));
          };

          return (
            <Dialog
              open
              onOpenChange={(open) => {
                if (!open) setOpenApiModal(null);
              }}
            >
              <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>{`Select ${modalProductName} Items`}</DialogTitle>
                </DialogHeader>

                {/* Count + batch controls */}
                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border pb-3">
                  <span className="text-xs text-muted-foreground">
                    {`${selectedItems.length} of ${allItems.length} selected`}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={selectAll}
                      disabled={selectedItems.length === allItems.length}
                    >
                      Select All
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={clearAll}
                      disabled={selectedItems.length === 0}
                    >
                      Clear All
                    </Button>
                  </div>
                </div>

                {/* Scrollable item list */}
                <div className="flex-1 overflow-y-auto">
                  <div className="flex flex-col divide-y divide-border">
                    {allItems.map((item) => {
                      const isChecked = selectedSet.has(item.code);
                      return (
                        <label
                          key={item.code}
                          className="flex cursor-pointer items-center justify-between gap-3 px-1 py-2.5 hover:bg-muted/40"
                        >
                          <span className="flex items-center gap-3">
                            <Checkbox checked={isChecked} onChange={() => toggleItem(item)} />
                            <span className="text-sm">{item.name}</span>
                          </span>
                          <span className="shrink-0 text-sm font-semibold text-foreground">
                            {inr(item.price)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Footer: total + done */}
                <div className="flex shrink-0 items-center justify-between border-t border-border pt-3">
                  <div className="text-sm">
                    <span className="text-muted-foreground">Total: </span>
                    <span className="font-bold">{inr(modalTotal)}</span>
                  </div>
                  <Button size="sm" onClick={() => setOpenApiModal(null)}>
                    Done
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          );
        })()}
    </div>
  );
}
